<!-- src/components/TransactionStats.vue -->
<template>
    <div class="transaction-stats">
      <div class="page-header">
        <div class="page-icon">📊</div>
        <h2>商品交易统计</h2>
      </div>
      <el-form inline class="filter-form">
        <el-form-item label="起始日期">
          <el-date-picker
            v-model="startDate"
            type="date"
            placeholder="选择起始日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker
            v-model="endDate"
            type="date"
            placeholder="选择结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchStats" :loading="loading">查询</el-button>
        </el-form-item>
      </el-form>
  
      <el-table
        :data="paginatedData"
        style="width: 100%"
        v-if="stats.length"
        border
        stripe
        :default-sort="{ prop: 'purchase_quantity', order: 'descending' }"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="product_name" label="商品名称" sortable width="200" align="center">
          <template #default="{ row }">
            <span class="product-name">{{ row.product_name }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="purchase_quantity" label="买入总量" sortable width="150" align="center">
          <template #default="{ row }">
            <span class="quantity purchase">{{ row.purchase_quantity || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sale_quantity" label="卖出总量" sortable width="150" align="center">
          <template #default="{ row }">
            <span class="quantity sale">{{ row.sale_quantity || 0 }}</span>
          </template>
        </el-table-column>
      </el-table>
  
      <!-- 汇总行 -->
      <div class="summary" v-if="stats.length">
        <span>总计：</span>
        <span class="quantity purchase">买入 {{ totalPurchase }} 条</span>
        <span class="quantity sale">卖出 {{ totalSale }} 条</span>
      </div>
  
      <!-- 分页 -->
      <el-pagination
        v-if="stats.length"
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[5, 10, 20, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="stats.length"
      />
  
      <div v-if="!stats.length" class="no-data">请查询数据</div>
    </div>
  </template>
  
  <script>
  import { ref, computed } from 'vue';
  import axios from 'axios';
import API_BASE from '@/api';
  import { ElMessage } from 'element-plus';
  
  export default {
    setup() {
      const startDate = ref('');
      const endDate = ref('');
      const stats = ref([]);
      const loading = ref(false);
      const currentPage = ref(1);
      const pageSize = ref(10);
      const sortProp = ref('purchase_quantity');
      const sortOrder = ref('descending');
  
      // 获取统计数据
      const fetchStats = async () => {
        if (!startDate.value || !endDate.value) {
          ElMessage.error('请选择起始日期和结束日期');
          return;
        }
  
        loading.value = true;
        try {
          const response = await axios.get(`${API_BASE}/api/product-transaction-stats`, {
            params: {
              startDate: startDate.value,
              endDate: endDate.value
            }
          });
  
          if (response.data.success) {
            stats.value = response.data.stats;
            if (stats.value.length === 0) {
              ElMessage.warning('该时间段内没有交易记录');
            }
          } else {
            ElMessage.error('获取统计数据失败');
          }
        } catch (error) {
          ElMessage.error('获取统计数据失败');
          console.error(error);
        } finally {
          loading.value = false;
        }
      };
  
      // 排序处理
      const handleSortChange = ({ prop, order }) => {
        sortProp.value = prop;
        sortOrder.value = order;
        currentPage.value = 1; // 排序后重置到第一页
      };
  
      // 分页处理
      const handleSizeChange = (val) => {
        pageSize.value = val;
        currentPage.value = 1; // 更改每页数量后重置到第一页
      };
  
      const handleCurrentChange = (val) => {
        currentPage.value = val;
      };
  
      // 排序后的数据
      const sortedData = computed(() => {
        const data = [...stats.value];
        if (sortProp.value && sortOrder.value) {
          data.sort((a, b) => {
            const valA = Number(a[sortProp.value] || 0);
            const valB = Number(b[sortProp.value] || 0);
            return sortOrder.value === 'ascending' ? valA - valB : valB - valA;
          });
        }
        return data;
      });
  
      // 分页数据
      const paginatedData = computed(() => {
        const start = (currentPage.value - 1) * pageSize.value;
        const end = start + pageSize.value;
        return sortedData.value.slice(start, end);
      });
  
      // 计算总计（强制转换为数值）
      const totalPurchase = computed(() => {
        return stats.value.reduce((sum, item) => sum + Number(item.purchase_quantity || 0), 0);
      });
  
      const totalSale = computed(() => {
        return stats.value.reduce((sum, item) => sum + Number(item.sale_quantity || 0), 0);
      });
  
      return {
        startDate,
        endDate,
        stats,
        loading,
        currentPage,
        pageSize,
        paginatedData,
        totalPurchase,
        totalSale,
        fetchStats,
        handleSortChange,
        handleSizeChange,
        handleCurrentChange
      };
    }
  };
  </script>
  
  <style scoped>
  .transaction-stats {
    padding: 0;
  }

  .filter-form {
    background-color: #fff;
    padding: 16px 20px;
    border-radius: var(--radius-md);
    margin-bottom: 16px;
    box-shadow: var(--shadow-card);
    border: 1px solid var(--border-lighter);
  }

  .el-table {
    background-color: #fff;
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .el-table th {
    background-color: var(--primary-lighter) !important;
    color: var(--text-primary) !important;
    font-weight: 600;
  }

  .el-table .cell {
    font-size: 14px;
    color: var(--text-secondary);
  }

  .product-name {
    color: var(--text-primary);
    font-weight: 500;
  }

  .quantity.purchase {
    color: var(--success);
    font-weight: 600;
  }

  .quantity.sale {
    color: var(--warning);
    font-weight: 600;
  }

  .summary {
    margin-top: 12px;
    padding: 12px 20px;
    background-color: #fff;
    border-radius: var(--radius-md);
    font-size: 14px;
    color: var(--text-secondary);
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    box-shadow: var(--shadow-card);
    border: 1px solid var(--border-lighter);
  }

  .pagination {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }

  .no-data {
    text-align: center;
    margin-top: 40px;
    color: var(--text-tertiary);
    font-size: 16px;
    padding: 60px 0;
    background: #fff;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-lighter);
  }
  </style>
